import { useContext, memo, useEffect, useCallback } from "react";
import { AppContext } from "src/api/AppContext";
import {
  CategoryOptionType,
  useGetProductGroupPageDetailLazyQuery,
} from "src/generated/graphql";
import {
  ScrollArea,
  Flex,
  Text,
  Box,
  Grid,
  Title,
  Pagination,
  Loader,
  Badge,
} from "@mantine/core";
import { MdCategory } from "react-icons/md";
import ProductCatalogSearch from "../ProductCatalogSearch";
import { formatter } from "src/utils/constants";
import { debounce } from "src/utils/utils";
import {
  CatalogContext,
  IProductsCatalog,
  Screens,
} from "src/api/context/CatalogContext";
import ShoppingCart from "../ShoppingCart";
import ProductCatalogDetail from "../ProductCatalogDetail";
import Checkout from "../Checkout";

export const calculateMinMax = (products: IProductsCatalog) => {
  if (!products[0]) return <Text size={"sm"}>No Product Found</Text>;
  if (products.length === 1)
    return <Text> {formatter.format(products[0].cost)}</Text>;
  let minProduct = products[0].cost;
  let maxProduct = products[0].cost;
  products.forEach((product, index) => {
    if (index !== 0) {
      const productValue = product.cost;
      if (productValue < minProduct) {
        minProduct = productValue;
      }
      if (productValue > maxProduct) {
        maxProduct = productValue;
      }
    }
  });
  return (
    <Text>
      {formatter.format(minProduct)} - {formatter.format(maxProduct)}
    </Text>
  );
};

function ProductCatalogListing() {
  const { currentOrganization, currentLocation } = useContext(AppContext);
  const {
    sortRevenue,
    handleSortChange,
    count,
    activePage,
    setPage,
    search,
    setSearch,
    selectedPG,
    setSelectedPG,
    productsCart,
    activeScreen,
  } = useContext(CatalogContext);

  const [getProductGroup, { data, loading }] =
    useGetProductGroupPageDetailLazyQuery({
      fetchPolicy: "network-only",
    });

  useEffect(() => {
    if (currentLocation?.id && !sortRevenue) {
      getProductGroup({
        variables: {
          locationId: currentLocation?.id,
          getByProductsCount: true,
          includeVirtual: true,
          pagination: { filters: [], count, page: activePage },
        },
      });
    } else if (sortRevenue) {
      sendRequestSearch(search);
    }
  }, [currentLocation, sortRevenue, activePage]);

  let filterRevenue = currentOrganization?.categoryOptions.filter(
    (e) => e.type === CategoryOptionType.Revenue
  );

  const sendRequestSearch = (value: string) => {
    getProductGroup({
      variables: {
        locationId: currentLocation?.id,
        getByProductsCount: true,
        pagination: {
          filters: [
            {
              field: "id",
              type: "text",
              value: sortRevenue,
              parentRelationField: "revenueCategory",
            },
            { field: "name", type: "text", value },
          ],
          count,
          page: activePage,
        },
      },
    });
  };

  const debouncedSendRequest = useCallback(
    debounce((value) => {
      sendRequestSearch(value);
    }, 800),
    [sortRevenue]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSendRequest(value);
  };

  const checkForProductGroup = useCallback(
    (groupId: string) => {
      let isPGInCart = productsCart.find((p) => p.productGroup.id === groupId);
      return !!isPGInCart;
    },
    [productsCart]
  );

  if (activeScreen === Screens.Checkout) return <Checkout />;
  if (selectedPG) return <ProductCatalogDetail />;
  return (
    <>
      <Grid m={0} sx={{}}>
        <Grid.Col sm={9} md={10} p={"md"}>
          <ProductCatalogSearch value={search} onChange={handleSearchChange} />
          <ScrollArea
            scrollbarSize={3}
            mt={"xs"}
            sx={{
              ".mantine-ScrollArea-scrollbar": {
                opacity: 0,
              },
            }}
          >
            <Flex gap={"md"}>
              <Flex
                onClick={() => handleSortChange("")}
                key={"all"}
                sx={(theme) => ({
                  height: 94,
                  width: 110,
                  color: theme.colors.primary[6],
                  cursor: "pointer",
                  background:
                    sortRevenue === ""
                      ? theme.colors.secondary[1]
                      : "transparent",
                  borderRadius: 4,
                  "&:hover": {
                    borderColor: theme.colors.secondaryTextColor[6],
                  },
                })}
                align={"center"}
                justify={"center"}
                direction={"column"}
              >
                <MdCategory size={50} />
                <Text
                  sx={(theme) => ({
                    color:
                      sortRevenue === ""
                        ? theme.colors.secondaryTextColor[1]
                        : theme.colors.secondaryTextColor[6],
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    width: "inherit",
                    textAlign: "center",
                  })}
                >
                  {"All"}
                </Text>
              </Flex>
              {filterRevenue?.map((e) => (
                <Flex
                  onClick={() => handleSortChange(e.id)}
                  key={e.id}
                  sx={(theme) => ({
                    height: 94,
                    width: 110,
                    color: theme.colors.primary[6],
                    cursor: "pointer",
                    background:
                      e.id === sortRevenue
                        ? theme.colors.secondary[1]
                        : "transparent",
                    borderRadius: 4,
                    "&:hover": {
                      borderColor: theme.colors.secondaryTextColor[6],
                    },
                  })}
                  align={"center"}
                  justify={"center"}
                  direction={"column"}
                >
                  <MdCategory size={50} />
                  <Text
                    sx={(theme) => ({
                      color:
                        e.id === sortRevenue
                          ? theme.colors.secondaryTextColor[1]
                          : theme.colors.secondaryTextColor[6],
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      width: "inherit",
                      textAlign: "center",
                    })}
                  >
                    {e.name}
                  </Text>
                </Flex>
              ))}
            </Flex>
          </ScrollArea>

          <ScrollArea scrollbarSize={3}>
            <Flex gap={"sm"} wrap={"wrap"} mt={7} sx={{ maxHeight: "62vh" }}>
              {data?.productGroupPage?.items?.map((group) => (
                <Flex
                  key={group.id}
                  sx={(theme) => ({
                    height: 220,
                    width: 220,
                    border: `2px solid ${theme.colors.secondary[1]}`,
                    color: theme.colors.secondaryTextColor[4],
                    borderRadius: 4,
                    position: "relative",
                  })}
                  align={"center"}
                  justify={"center"}
                  direction={"column"}
                  onClick={() => setSelectedPG(group)}
                >
                  <Box
                    sx={(theme) => ({
                      height: 120,
                      width: 120,
                      background: theme.colors.secondary[6],
                      borderRadius: 4,
                      overflow: "hidden",
                    })}
                  >
                    {group.imgUrl && (
                      <img
                        src={group.imgUrl || ""}
                        style={{
                          height: "100%",
                          width: "100%",
                          objectFit: "cover",
                        }}
                        alt="Product Image"
                      />
                    )}
                  </Box>

                  <Text
                    sx={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      width: "inherit",
                      textAlign: "center",
                      lineHeight: 1,
                      paddingTop: 2,
                    }}
                  >
                    {group.name}
                  </Text>

                  {calculateMinMax(group.products)}

                  {checkForProductGroup(group.id) && (
                    <Badge
                      size="md"
                      mt={2}
                      sx={(theme) => ({
                        background: theme.colors.primary[6],
                        color: theme.colors.primaryTextColor[6],
                        textTransform: "none",
                      })}
                    >
                      In Cart
                    </Badge>
                  )}
                </Flex>
              ))}

              {loading ? (
                <Loader w={"100%"} sx={{ textAlign: "center" }} size={"xl"} />
              ) : (
                data?.productGroupPage?.items.length === 0 && (
                  <Title order={3} align="center" w={"100%"}>
                    No Product Group Found
                  </Title>
                )
              )}
            </Flex>
          </ScrollArea>

          <Box
            sx={{
              display: "inline-flex",
              width: "100%",
              justifyContent: "center",
            }}
            mt={"xs"}
          >
            {!!data?.productGroupPage?.totalRows && (
              <Pagination
                page={activePage}
                onChange={setPage}
                total={Math.ceil(data.productGroupPage.totalRows / count)}
              />
            )}
          </Box>
        </Grid.Col>
        <ShoppingCart />
      </Grid>
    </>
  );
}

export default memo(ProductCatalogListing);
